import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, firstValueFrom, map, skip, startWith, Subject, take, takeUntil } from 'rxjs';
import { cycleStatusBadgeVariant, cycleStatusLabel } from '../../notification/cycle-status.util';
import { resolveAlertConfig } from '../../configuration/configuration.defaults';
import { ActiveCampaignCardComponent } from '../active-campaign-card/active-campaign-card.component';
import { ActiveCampaignGroup } from '../active-campaign-card/active-campaign-card.util';
import { AuthCognitoService } from '../../auth/auth-cognito.service';
import { subjectLabels } from '../../configuration/subject-label.util';
import {
  AlertCampaignListItem,
  AlertCampaignService,
} from '../alert-roster/alert-campaign.service';
import { alertHistoryHeader } from '../callback-location.util';
import { ContactsViewModel } from '../contacts.view-model';
import { OrganizationsComponent } from '../organizations/organizations.component';
import {
  ORG_ALERT_HISTORY_CAMPAIGN_ALL,
  ORG_ALERT_HISTORY_CAMPAIGN_MANUAL,
  OrgAlertHistoryItem,
  OrgAlertHistoryRequest,
} from '../patient.model';
import { PatientService } from '../patient.service';
import { Organization, UpdateProfileRequest } from '../user.service';
import { FormUtils } from 'src/app/shared/utils/form-utils';
import { AlertStatusLegendComponent } from 'src/app/notification/alert-status-legend/alert-status-legend.component';
import { DashboardSectionHeaderComponent } from 'src/app/notification/dashboard/dashboard-section-header/dashboard-section-header.component';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { SkeletonComponent } from 'src/app/shared/skeleton/skeleton.component';
import {
  ALERT_HISTORY_EXPORT_ROW_CAP,
  alertHistoryExportFileName,
  downloadAlertHistoryCsv,
  downloadAlertHistoryXlsx,
  historyPageOffsetToken,
  localDayRangeMs,
} from './org-alert-history-export.util';
import {
  CachedAlertHistoryState,
  OrgAlertHistoryCacheService,
} from './org-alert-history-cache.service';

const PAGE_SIZE = 50;

type ActiveFilterSnapshot = Omit<OrgAlertHistoryRequest, 'nextPage' | 'pageSize' | 'status'>;
type AlertHistorySortKey = 'initiatedOn' | 'status';

@Component({
  selector: 'app-org-alert-history-list',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    DashboardSectionHeaderComponent,
    AlertStatusLegendComponent,
    ActiveCampaignCardComponent,
    OrganizationsComponent,
    SkeletonComponent,
  ],
  templateUrl: './org-alert-history-list.component.html',
  styleUrl: './org-alert-history-list.component.scss',
  host: {
    class: 'contact-list',
  },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class OrgAlertHistoryListComponent implements OnInit, OnDestroy {
  #snackBar = inject(MatSnackBar);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #applyingNavigation = false;
  #authCognitoService = inject(AuthCognitoService);
  #patientService = inject(PatientService);
  #contactsViewModel = inject(ContactsViewModel);
  #campaignService = inject(AlertCampaignService);
  #historyCache = inject(OrgAlertHistoryCacheService);
  #restoredOrgId: string | null = null;

  readonly pageSize = PAGE_SIZE;
  readonly campaignAll = ORG_ALERT_HISTORY_CAMPAIGN_ALL;
  readonly campaignManual = ORG_ALERT_HISTORY_CAMPAIGN_MANUAL;
  filterForm = new FormGroup({
    campaignId: new FormControl<string>(ORG_ALERT_HISTORY_CAMPAIGN_ALL, { nonNullable: true }),
    dateRange: new FormGroup({
      start: new FormControl<Date | null>(null),
      end: new FormControl<Date | null>(null),
    }),
    search: new FormControl('', { nonNullable: true }),
    callSequenceId: new FormControl('', { nonNullable: true }),
  });

  alerts = signal<OrgAlertHistoryItem[]>([]);
  historySortKey = signal<AlertHistorySortKey>('initiatedOn');
  historySortDirection = signal<'asc' | 'desc'>('desc');
  sortedAlerts = computed(() =>
    sortAlertHistoryItems(this.alerts(), this.historySortKey(), this.historySortDirection()),
  );
  alertsLoading = signal(false);
  exportLoading = signal(false);
  hasRun = signal(false);
  totalCount = signal<number | null>(null);
  pageIndex = signal(0);
  hasNextPage = signal(false);
  campaigns = signal<AlertCampaignListItem[]>([]);
  campaignsLoading = signal(false);

  #activeFilters = signal<ActiveFilterSnapshot | null>(null);

  locationsLoading = toSignal(this.#contactsViewModel.locationsLoading$);
  locations = toSignal(this.#contactsViewModel.locations$, { initialValue: [] });
  profile$ = this.#contactsViewModel.profile$;
  profileError$ = this.#contactsViewModel.profileError$;
  profile = toSignal(this.profile$);
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  readonly #filterValues = toSignal(
    this.filterForm.valueChanges.pipe(
      startWith(this.filterForm.getRawValue()),
      map(() => this.filterForm.getRawValue()),
    ),
    { initialValue: this.filterForm.getRawValue() },
  );

  callSequenceOnlyRun = computed(() => {
    const id = this.#filterValues().callSequenceId?.trim() ?? '';
    return id.length > 0;
  });

  campaignOnlyRun = computed(() => {
    if (this.callSequenceOnlyRun()) {
      return false;
    }
    const campaignId = this.#filterValues().campaignId ?? ORG_ALERT_HISTORY_CAMPAIGN_ALL;
    return this.isSpecificCampaignSelected(campaignId);
  });

  dateRangeRequired = computed(() => !this.campaignOnlyRun() && !this.callSequenceOnlyRun());

  orgAlertConfig = computed(() => resolveAlertConfig(this.currentOrganization()?.alertConfig));

  showCampaignSummaryCard = computed(() => {
    if (!this.hasRun() || this.callSequenceOnlyRun()) {
      return false;
    }
    const campaignId =
      this.#activeFilters()?.campaignId ?? this.#filterValues().campaignId ?? ORG_ALERT_HISTORY_CAMPAIGN_ALL;
    return this.isSpecificCampaignSelected(campaignId);
  });

  campaignSummaryGroup = computed((): ActiveCampaignGroup | null => {
    if (!this.showCampaignSummaryCard()) {
      return null;
    }
    const campaignId =
      this.#activeFilters()?.campaignId ?? this.#filterValues().campaignId ?? '';
    if (!this.isSpecificCampaignSelected(campaignId)) {
      return null;
    }
    const title = this.campaigns().find((c) => c.id === campaignId)?.title?.trim() || campaignId;
    return { campaignId, title, rows: [] };
  });

  canRun = computed(() => {
    if (this.alertsLoading()) {
      return false;
    }
    if (this.callSequenceOnlyRun()) {
      return true;
    }
    const { campaignId, dateRange } = this.#filterValues();
    const id = campaignId ?? ORG_ALERT_HISTORY_CAMPAIGN_ALL;
    if (this.isSpecificCampaignSelected(id)) {
      return true;
    }
    return Boolean(dateRange?.start && dateRange?.end);
  });

  canExport = computed(
    () => this.hasRun() && !this.alertsLoading() && !this.exportLoading() && this.alerts().length > 0,
  );

  @ViewChild('drawer', { static: true })
  organizationMenu!: MatSidenav;

  destroy$ = new Subject<void>();

  subjectSingular(): string {
    return subjectLabels(this.currentOrganization()).singular;
  }

  alertHistoryHeaderText(): string {
    return alertHistoryHeader(this.totalCount(), this.alertsLoading(), this.hasRun());
  }

  ngOnInit(): void {
    this.filterForm.controls.campaignId.valueChanges
      .pipe(
        filter(() => !this.#applyingNavigation),
        takeUntil(this.destroy$),
      )
      .subscribe((selectedCampaignId) => {
        this.clearCampaignQueryParamsIfStale(selectedCampaignId);
        this.applyHistoryFilterMode();
        if (!this.#applyingNavigation && this.hasRun()) {
          const ranFor = this.#activeFilters()?.campaignId ?? ORG_ALERT_HISTORY_CAMPAIGN_ALL;
          if (ranFor !== selectedCampaignId) {
            this.resetResults();
          }
        }
      });

    this.applyHistoryFilterMode();

    this.filterForm.controls.callSequenceId.valueChanges
      .pipe(
        filter(() => !this.#applyingNavigation),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.applyHistoryFilterMode();
        if (this.hasRun()) {
          this.resetResults();
        }
      });

    this.#route.queryParamMap.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
      if (!this.profile()) {
        return;
      }
      if (this.#route.snapshot.queryParamMap.get('run') === '1') {
        this.#restoredOrgId = null;
      }
      this.initializeFiltersFromNavigation();
    });

    this.profile$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadCampaignOptions();
        this.#contactsViewModel.loadLocations();
        this.initializeFiltersFromNavigation();
      });

    this.profile$
      .pipe(
        take(1),
        filter((profile) => !profile),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadProfile();
      });

    this.profileError$
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => !!e),
      )
      .subscribe(() => {
        const ref = this.#snackBar.open("Sorry, we couldn't load the user information.", 'Okay', {
          duration: 5000,
        });
        ref.afterDismissed().subscribe(() => {
          this.onLogout();
        });
      });
  }

  ngOnDestroy(): void {
    this.persistCache();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCampaignOptions(): void {
    this.campaignsLoading.set(true);
    this.#campaignService.listCampaigns().subscribe({
      next: ({ result }) => {
        this.campaigns.set(result ?? []);
        this.campaignsLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading campaigns', err);
        this.campaigns.set([]);
        this.campaignsLoading.set(false);
      },
    });
  }

  runSearch(): void {
    const range = this.filterForm.controls.dateRange;
    const start = range.controls.start.value;
    const end = range.controls.end.value;
    const campaignId = this.filterForm.controls.campaignId.value;
    const callSequenceId = this.filterForm.controls.callSequenceId.value.trim();
    const specificCampaign = this.isSpecificCampaignSelected(campaignId);
    const search =
      specificCampaign || callSequenceId ? '' : this.filterForm.controls.search.value.trim();

    if (callSequenceId) {
      const filters: ActiveFilterSnapshot = { callSequenceId };
      this.#activeFilters.set(filters);
      this.pageIndex.set(0);
      this.hasRun.set(false);
      this.fetchPage(0, filters);
      return;
    }

    if (!specificCampaign && (!start || !end)) {
      this.#snackBar.open('Choose a start and end date.', 'Okay', { duration: 4000 });
      return;
    }
    if (!specificCampaign && ((start && !end) || (!start && end))) {
      this.#snackBar.open('Choose both start and end date, or leave the range empty.', 'Okay', {
        duration: 4000,
      });
      return;
    }
    if (!specificCampaign && start && end && start.getTime() > end.getTime()) {
      this.#snackBar.open('Start date must be on or before end date.', 'Okay', { duration: 4000 });
      return;
    }

    const filters: ActiveFilterSnapshot = {
      ...(campaignId !== ORG_ALERT_HISTORY_CAMPAIGN_ALL ? { campaignId } : {}),
      ...(search ? { search } : {}),
    };
    if (!specificCampaign && start && end) {
      const { dateFrom, dateTo } = localDayRangeMs(start, end);
      filters.dateFrom = dateFrom;
      filters.dateTo = dateTo;
    }

    this.#activeFilters.set(filters);
    this.pageIndex.set(0);
    this.hasRun.set(false);
    this.fetchPage(0, filters);
  }

  goToNextPage(): void {
    if (!this.hasNextPage() || this.alertsLoading()) {
      return;
    }
    const filters = this.#activeFilters();
    if (!filters) {
      return;
    }
    const nextIndex = this.pageIndex() + 1;
    this.fetchPage(nextIndex, filters);
  }

  goToPreviousPage(): void {
    if (this.pageIndex() <= 0 || this.alertsLoading()) {
      return;
    }
    const filters = this.#activeFilters();
    if (!filters) {
      return;
    }
    this.fetchPage(this.pageIndex() - 1, filters);
  }

  private fetchPage(page: number, filters: ActiveFilterSnapshot): void {
    this.alertsLoading.set(true);
    this.#patientService
      .getOrgAlertHistory({
        pageSize: PAGE_SIZE,
        nextPage: historyPageOffsetToken(page, PAGE_SIZE),
        ...filters,
      })
      .subscribe({
        next: ({ result, nextPage, totalCount }) => {
          this.alerts.set(result);
          this.totalCount.set(totalCount ?? result.length);
          this.pageIndex.set(page);
          this.hasNextPage.set(Boolean(nextPage));
          this.alertsLoading.set(false);
          this.hasRun.set(true);
          this.persistCache();
        },
        error: (err) => {
          console.error('Error loading org alert history', err);
          this.alertsLoading.set(false);
          this.#snackBar.open('Could not load alert history. Please try again.', 'Okay', {
            duration: 5000,
          });
        },
      });
  }

  clearFiltersAndResults(): void {
    this.filterForm.reset({
      campaignId: ORG_ALERT_HISTORY_CAMPAIGN_ALL,
      dateRange: { start: null, end: null },
      search: '',
      callSequenceId: '',
    });
    this.applyHistoryFilterMode();
    this.resetResults();
    this.#historyCache.clear(this.currentOrgId());
    this.#restoredOrgId = null;
  }

  async exportCsv(): Promise<void> {
    await this.exportHistory('csv');
  }

  async exportXlsx(): Promise<void> {
    await this.exportHistory('xlsx');
  }

  private async exportHistory(format: 'csv' | 'xlsx'): Promise<void> {
    const filters = this.#activeFilters();
    const range = this.filterForm.controls.dateRange;
    const start = range.controls.start.value;
    const end = range.controls.end.value;
    if (!filters) {
      return;
    }

    this.exportLoading.set(true);
    try {
      const rows: OrgAlertHistoryItem[] = [];
      let page = 0;
      let truncated = false;

      while (rows.length < ALERT_HISTORY_EXPORT_ROW_CAP) {
        const { result, nextPage } = await firstValueFrom(
          this.#patientService.getOrgAlertHistory({
            pageSize: 100,
            nextPage: historyPageOffsetToken(page, 100),
            ...filters,
          }),
        );
        rows.push(...result);
        if (!nextPage || rows.length >= ALERT_HISTORY_EXPORT_ROW_CAP) {
          if (nextPage && rows.length >= ALERT_HISTORY_EXPORT_ROW_CAP) {
            truncated = true;
            rows.splice(ALERT_HISTORY_EXPORT_ROW_CAP);
          }
          break;
        }
        page += 1;
      }

      if (!rows.length) {
        this.#snackBar.open('No rows to export for the current filters.', 'Okay', { duration: 4000 });
        return;
      }

      const campaignTitle = filters.campaignId
        ? this.campaigns().find((c) => c.id === filters.campaignId)?.title
        : undefined;
      const baseName = alertHistoryExportFileName(
        this.currentOrganization()?.description ?? 'org',
        start && end ? { from: start, to: end } : undefined,
        campaignTitle,
      );
      const subjectLabel = this.subjectSingular();
      if (format === 'csv') {
        downloadAlertHistoryCsv(rows, subjectLabel, `${baseName}.csv`);
      } else {
        downloadAlertHistoryXlsx(rows, subjectLabel, `${baseName}.xlsx`);
      }

      if (truncated) {
        this.#snackBar.open(
          `Export limited to ${ALERT_HISTORY_EXPORT_ROW_CAP} rows. Narrow your date range for a full export.`,
          'Okay',
          { duration: 7000 },
        );
      }
    } catch (err) {
      console.error('Export failed', err);
      this.#snackBar.open('Export failed. Please try again.', 'Okay', { duration: 5000 });
    } finally {
      this.exportLoading.set(false);
    }
  }

  formatPhone(phone: string | undefined): string {
    if (!phone) {
      return '';
    }
    return FormUtils.formatPhoneNumber(phone);
  }

  formatDate(epochMs: number): string {
    return new Date(epochMs).toLocaleString();
  }

  contactAttemptedLine(alert: OrgAlertHistoryItem): string {
    const name = alert.contactName?.trim();
    const phone =
      alert.contactPhone && alert.contactPhone !== 'Unknown'
        ? this.formatPhone(alert.contactPhone)
        : '';

    if (name && name !== 'Unknown' && phone) {
      return `${name} ${phone}`;
    }
    if (name && name !== 'Unknown') {
      return name;
    }
    if (phone) {
      return phone;
    }
    return 'Unknown';
  }

  alertStatusLabel(status: string): string {
    return cycleStatusLabel(status);
  }

  alertStatusBadgeVariant(status: string): string {
    return cycleStatusBadgeVariant(status);
  }

  toggleHistorySort(key: AlertHistorySortKey): void {
    if (this.historySortKey() === key) {
      this.historySortDirection.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.historySortKey.set(key);
      this.historySortDirection.set(key === 'initiatedOn' ? 'desc' : 'asc');
    }
  }

  historySortIcon(key: AlertHistorySortKey): string | null {
    if (this.historySortKey() !== key) {
      return null;
    }
    return this.historySortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  openAlertLog(alert: OrgAlertHistoryItem): void {
    this.persistCache();
    this.#router.navigate(['/contact'], {
      queryParams: {
        patientId: alert.patientId,
        patientName: alert.patientName,
        providerId: alert.providerId,
        sequenceId: alert.id,
      },
    });
  }

  onOrganizationSelected(org: Organization): void {
    this.organizationMenu.toggle();
    if (org.id !== this.profile()?.currentOrg) {
      const args: UpdateProfileRequest = {
        name: this.profile()?.name || '',
        currentOrg: org.id,
      };
      this.#contactsViewModel.updateOrganization(args, org);
      this.resetFilterFormDefaults();
      this.resetResults();
      this.#restoredOrgId = null;
      this.loadCampaignOptions();
      this.tryRestoreFromCache();
    }
  }

  private resetResults(): void {
    this.alerts.set([]);
    this.hasRun.set(false);
    this.totalCount.set(null);
    this.pageIndex.set(0);
    this.hasNextPage.set(false);
    this.#activeFilters.set(null);
  }

  onLogout(): void {
    this.#authCognitoService.signOut().then(() => {
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('organizationId');
    });
  }

  private applyHistoryFilterMode(): void {
    const campaignId = this.filterForm.controls.campaignId.value;
    const callSequenceId = this.filterForm.controls.callSequenceId.value.trim();

    if (callSequenceId) {
      this.filterForm.controls.campaignId.disable({ emitEvent: false });
      this.filterForm.controls.dateRange.disable({ emitEvent: false });
      this.filterForm.controls.search.disable({ emitEvent: false });
      this.filterForm.controls.callSequenceId.enable({ emitEvent: false });
      return;
    }

    this.filterForm.controls.callSequenceId.enable({ emitEvent: false });

    if (this.isSpecificCampaignSelected(campaignId)) {
      this.filterForm.patchValue(
        { dateRange: { start: null, end: null }, search: '', callSequenceId: '' },
        { emitEvent: false },
      );
      this.filterForm.controls.campaignId.enable({ emitEvent: false });
      this.filterForm.controls.dateRange.disable({ emitEvent: false });
      this.filterForm.controls.search.disable({ emitEvent: false });
    } else {
      this.filterForm.controls.campaignId.enable({ emitEvent: false });
      this.filterForm.controls.dateRange.enable({ emitEvent: false });
      this.filterForm.controls.search.enable({ emitEvent: false });
    }
  }

  private isSpecificCampaignSelected(campaignId: string): boolean {
    return (
      campaignId !== ORG_ALERT_HISTORY_CAMPAIGN_ALL &&
      campaignId !== ORG_ALERT_HISTORY_CAMPAIGN_MANUAL
    );
  }

  private currentOrgId(): string | undefined {
    return this.profile()?.currentOrg ?? this.currentOrganization()?.id;
  }

  private initializeFiltersFromNavigation(): void {
    const orgId = this.currentOrgId();
    if (!orgId) {
      return;
    }

    const campaignId = this.#route.snapshot.queryParamMap.get('campaignId');
    const shouldRun = this.#route.snapshot.queryParamMap.get('run') === '1';

    if (campaignId && this.isSpecificCampaignSelected(campaignId) && shouldRun) {
      this.#applyingNavigation = true;
      this.filterForm.patchValue({
        campaignId,
        dateRange: { start: null, end: null },
        search: '',
      });
      this.applyHistoryFilterMode();
      this.#applyingNavigation = false;
      this.#restoredOrgId = orgId;
      this.runSearch();
      void this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { run: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return;
    }

    if (orgId === this.#restoredOrgId) {
      return;
    }
    this.tryRestoreFromCache();
  }

  private clearCampaignQueryParamsIfStale(selectedCampaignId: string): void {
    const urlCampaignId = this.#route.snapshot.queryParamMap.get('campaignId');
    const urlRun = this.#route.snapshot.queryParamMap.get('run');
    if (!urlCampaignId && !urlRun) {
      return;
    }
    if (urlCampaignId === selectedCampaignId && !urlRun) {
      return;
    }
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { campaignId: null, run: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private tryRestoreFromCache(): void {
    const orgId = this.currentOrgId();
    if (!orgId || orgId === this.#restoredOrgId) {
      return;
    }
    const cached = this.#historyCache.load(orgId);
    this.#restoredOrgId = orgId;
    if (!cached?.hasRun) {
      return;
    }
    this.applyCachedState(cached);
  }

  private applyCachedState(cached: CachedAlertHistoryState): void {
    const campaignOnly = this.isSpecificCampaignSelected(cached.campaignId);
    const sequenceOnly = Boolean(cached.callSequenceId?.trim());
    this.filterForm.patchValue({
      campaignId: cached.campaignId,
      search: campaignOnly || sequenceOnly ? '' : cached.search,
      callSequenceId: cached.callSequenceId ?? '',
      dateRange: campaignOnly || sequenceOnly
        ? { start: null, end: null }
        : {
            start: cached.dateStart ? new Date(cached.dateStart) : null,
            end: cached.dateEnd ? new Date(cached.dateEnd) : null,
          },
    });
    this.applyHistoryFilterMode();
    this.#activeFilters.set(cached.activeFilters);
    this.alerts.set(cached.alerts);
    this.totalCount.set(cached.totalCount);
    this.pageIndex.set(cached.pageIndex);
    this.hasNextPage.set(cached.hasNextPage);
    this.hasRun.set(cached.hasRun);
  }

  private persistCache(): void {
    if (!this.hasRun()) {
      return;
    }
    const range = this.filterForm.controls.dateRange;
    const start = range.controls.start.value;
    const end = range.controls.end.value;
    const state: CachedAlertHistoryState = {
      campaignId: this.filterForm.controls.campaignId.value,
      dateStart: start ? start.toISOString() : null,
      dateEnd: end ? end.toISOString() : null,
      search: this.filterForm.controls.search.value,
      callSequenceId: this.filterForm.controls.callSequenceId.value,
      activeFilters: this.#activeFilters(),
      alerts: this.alerts(),
      totalCount: this.totalCount(),
      pageIndex: this.pageIndex(),
      hasNextPage: this.hasNextPage(),
      hasRun: this.hasRun(),
    };
    this.#historyCache.save(this.currentOrgId(), state);
  }

  private resetFilterFormDefaults(): void {
    this.filterForm.reset({
      campaignId: ORG_ALERT_HISTORY_CAMPAIGN_ALL,
      dateRange: { start: null, end: null },
      search: '',
      callSequenceId: '',
    });
    this.applyHistoryFilterMode();
  }
}

function sortAlertHistoryItems(
  items: OrgAlertHistoryItem[],
  key: AlertHistorySortKey,
  direction: 'asc' | 'desc',
): OrgAlertHistoryItem[] {
  const sorted = [...items];
  const mult = direction === 'asc' ? 1 : -1;
  sorted.sort((a, b) => {
    if (key === 'initiatedOn') {
      return (a.initiatedOn - b.initiatedOn) * mult;
    }
    return (
      cycleStatusLabel(a.status).localeCompare(cycleStatusLabel(b.status), undefined, {
        sensitivity: 'base',
      }) * mult
    );
  });
  return sorted;
}
